import styled from 'styled-components';
import { em, Group } from '@mantine/core';

/**
 * The modal surface. react-modal's own content element is reset to a bare wrapper (see
 * SendDocumentsModal), so this decides the size. Near-full-screen per the design: the picker is
 * a working surface, not a dialog. The max-* caps stop it stretching on an ultrawide display.
 */
export const ModalContent = styled.div`
	display: flex;
	flex-direction: column;
	/* No gap: the footer is the bottom band of the table card and has to sit flush against it.
	   The header supplies its own spacing instead. */
	gap: 0;
	padding: 1rem;
	/* Figma SDW/S1 puts a 3.75rem gutter each side of a 112.5rem frame, kept here as viewport
	   offsets so the width holds at other sizes. Its 4.8125rem top and bottom are not: an
	   expanded row is two columns of settings plus the Delivery block, and on a laptop that
	   left the panel scrolling inside a modal that was itself scrolling. 2rem each side. */
	width: calc(100vw - 7.5rem);
	max-width: 112.5rem;
	height: calc(100vh - 4rem);
	max-height: 75rem;
	background-color: ${({ theme }) => theme.v2.color.white[500]};
	border-radius: ${em(8)};
	overflow: hidden;
`;

/** "Send documents" title bar at the top of the modal, with the upload action opposite it. */
export const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1rem;
`;

/**
 * Figma SDW/S1 sizes the email note at 44rem rather than letting it take the free width, so the
 * footer reads as a field on the left and the action on the right rather than one stretched bar.
 */
export const EmailMessageField = styled.div`
	width: 44rem;
	max-width: 100%;
`;

/**
 * Picker footer, per Figma's FOOTER frame: `space-between` with the email note on the left and
 * the send action on the right. The confirm step keeps `FooterActionsRow` below, which stacks its
 * back/send pair to the right instead.
 */
export const PickerFooter = styled(Group)`
	justify-content: space-between;
	align-items: center;
	/* Figma's FOOTER frame: 1rem all round on a greys ground, closing the table card — hence the
	   bottom radius and the side borders that carry the card's edge down past the last row. */
	padding: 1rem;
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
	border-radius: 0 0 0.25rem 0.25rem;
`;

/**
 * Takes the space between header and footer so the document list scrolls within the modal
 * rather than growing it. `min-height: 0` is required or the flex child refuses to shrink.
 */
export const PickerRegion = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	min-height: 0;
	/* The table scrolls inside itself (below), so a second scrollbar here would only ever fight
	   it — and did, before the modal was made taller. */
	overflow: hidden;

	/* mantine-react-table lays itself out as toolbar / scroll area / pagination, each sized to
	   its content. Left alone the whole card sits at the top of a tall modal and strands the
	   footer under a band of white, so the card takes the region and the rows take the slack. */
	& > .mantine-table-container {
		flex: 1;
		min-height: 0;
	}

	& .mantine-table-scroll-area {
		flex: 1;
		min-height: 0;
	}
`;

/** Padded shell for a single step's content (confirm/success panels). */
export const StepContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.5rem;
`;

/** Bottom action bar shared by all three steps. */
export const FooterActionsRow = styled(Group)`
	justify-content: flex-end;
	align-items: flex-end;
	padding: 1rem 1.5rem;
	border-top: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
`;

/**
 * Confirm step's summary card, per Figma's "Summary section" frame: what is going out, to how
 * many, and exactly which documents — so the step is a real last look rather than a bare count.
 */
export const SummaryCard = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.75rem;
	padding: 1rem;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
	border-radius: 0.5rem;
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
`;

/** "Send N documents" line, with the document and recipient icons that precede it. */
export const SummaryTitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

export const SummaryDocumentList = styled.div`
	display: flex;
	flex-direction: column;
	align-self: stretch;
	gap: 0.25rem;
`;

/** One document. The name truncates rather than wrapping, so a long file name cannot push the
 *  card's height around. */
export const SummaryDocumentItem = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	min-width: 0;

	& > span {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
`;
